import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Download, Loader2, Scissors, Share2, X } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { fileUrl } from "./HeroSlides";

const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

async function shareClip(url, name) {
  try {
    const blob = await (await fetch(url)).blob();
    const file = new File([blob], `${name}.mp4`, { type: "video/mp4" });
    if (navigator.canShare?.({ files: [file] })) { await navigator.share({ files: [file], title: name }); return; }
  } catch (e) { if (e?.name === "AbortError") return; }
  const a = document.createElement("a");
  a.href = url; a.download = `${name}.mp4`; a.click();
  toast.message("Saved to your device. Post it from your gallery.");
}

export function HighlightMaker({ slug, post, onClose }) {
  const [s, setS] = useState(null);
  const [start, setStart] = useState(0);
  const [busy, setBusy] = useState(false);
  const [clip, setClip] = useState(null);
  const ref = useRef();
  useEffect(() => {
    api.get(`/public/weddings/${slug}/memories/${post.item_id}/highlight-suggest`).then((r) => { setS(r.data); setStart(r.data.start); })
      .catch((e) => { toast.error(formatApiError(e)); onClose(); });
  }, [slug, post.item_id]); // eslint-disable-line react-hooks/exhaustive-deps
  const seek = (v) => { setStart(v); if (ref.current) { ref.current.currentTime = v; ref.current.play().catch(() => {}); } };
  async function make() {
    setBusy(true);
    try { const { data } = await api.post(`/public/weddings/${slug}/memories/${post.item_id}/highlight`, { start }); setClip(fileUrl(data.path)); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }
  const max = s ? Math.max(0, s.duration - s.clip) : 0;
  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" data-testid="highlight-maker">
      <div className="w-full max-w-sm bg-[#111] rounded-3xl p-5 text-white space-y-4">
        <div className="flex items-center justify-between"><p className="font-serif text-xl flex items-center gap-2"><Scissors className="w-4 h-4 text-[var(--gold)]" /> 30-second highlight</p><button onClick={onClose} data-testid="highlight-close" aria-label="Close"><X className="w-5 h-5" /></button></div>
        {!s ? <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[var(--gold)]" /></div> : clip ? (
          <>
            <video src={clip} controls autoPlay playsInline className="w-full max-h-[55vh] rounded-2xl bg-black" data-testid="highlight-result" />
            <div className="grid grid-cols-2 gap-3">
              <button onClick={() => shareClip(clip, `${slug}-highlight`)} className="btn-gold !py-3 flex items-center justify-center gap-2" data-testid="highlight-share"><Share2 className="w-4 h-4" /> Share</button>
              <a href={clip} download={`${slug}-highlight.mp4`} className="btn-outline-gold !py-3 flex items-center justify-center gap-2 !text-[var(--gold)]" data-testid="highlight-download"><Download className="w-4 h-4" /> Save</a>
            </div>
            <button onClick={() => setClip(null)} className="text-xs text-white/60 w-full" data-testid="highlight-again">Pick a different moment</button>
          </>
        ) : (
          <>
            <video ref={ref} src={fileUrl(post.path)} playsInline muted={false} className="w-full max-h-[45vh] rounded-2xl bg-black" onLoadedMetadata={(e) => { e.currentTarget.currentTime = start; }} data-testid="highlight-preview" />
            {max > 0 ? (
              <div className="space-y-1">
                <input type="range" min={0} max={max} step={1} value={start} onChange={(e) => seek(Number(e.target.value))} className="w-full accent-[var(--gold)]" data-testid="highlight-start-slider" />
                <p className="text-xs text-white/60 flex justify-between"><span data-testid="highlight-range">{fmt(start)} – {fmt(Math.min(s.duration, start + s.clip))}</span>{start === s.start && <span className="text-[var(--gold)]">Loudest moment</span>}</p>
              </div>
            ) : <p className="text-xs text-white/60">This video is under 30 seconds, so the whole thing becomes your highlight.</p>}
            <button onClick={make} disabled={busy} className="btn-gold w-full !py-3 flex items-center justify-center gap-2" data-testid="highlight-make">{busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Making your clip…</> : "Make highlight"}</button>
          </>
        )}
      </div>
    </div>
  );
}
